// Wallet discovery and connection (injected wallets, EIP-6963 Phantom, the
// Coinbase smart wallet), the Base chain switch, ENS/basename profile and the
// cached ethers provider/signer. Mixed into App.vue; moved verbatim from it.
//
// Several branches are deliberate workarounds for specific wallets (Coinbase,
// Phantom, the 5.5 s provider scan in App.vue's mounted): keep them unless a
// change is verified on that wallet.
/*global ethers*/
/*global CoinbaseWalletSDK*/
import utils from '../utils';
import config from '../config';
import constants from '../constants';
import {
  log, trackEvent, DO_LOG, BASE_CHAIN_ID, BASE_CHAIN_PARAMS, TOKEN_IMAGE_URL, BASENAME_L2_RESOLVER_ADDRESS,
} from './shared';

// Providers whose accountsChanged/chainChanged we already listen to. Phantom
// re-announces itself on every eip6963:requestProvider.
const listenedProviders = new WeakSet();

const isUnknownChainError = (e) => !!e && (
  e.code === 4902 ||
  (e.data && e.data.originalError && e.data.originalError.code === 4902) ||
  /unrecognized chain/i.test(e.message || '')
);

const infuraProvider = new ethers.InfuraProvider("mainnet", "64e05046ef9d4b55866de25292aee3df");

export default {
  methods: {
    connectWallet: async function() {
      trackEvent('connectWallet');
      log('connectWallet');
      this.$bvModal.show('modal-connect');
      this.loadProviders();
      try {
        window.dispatchEvent(new Event("eip6963:requestProvider"));
      } catch (error) {
        log('Error requesting provider', error);
      }
    },
    createWallet: async function() {
      trackEvent('createWallet');
      log('create Wallet');
      this.isConnecting = true;
      
      try {
        const coinbaseWalletSDK = new CoinbaseWalletSDK({
          appName: 'Million Bit Homepage',
          appLogoUrl: 'https://millionbithomepage.com/static/mbh.png',
          appChainIds: [8453],
        });
        const coinbaseWalletProvider = await coinbaseWalletSDK.makeWeb3Provider();
        this.provider = coinbaseWalletProvider;
        log('coinbaseWalletProvider', coinbaseWalletProvider);
        const [address] = await coinbaseWalletProvider.request({
          method: 'eth_requestAccounts',
        });
        log('created Wallet', address, coinbaseWalletProvider);
        await this.setConnectedAccount(address);
        // await this.ensureBaseChain(this.provider);

        // const capabilities = await coinbaseWalletProvider.request({
        //   method: 'wallet_getCapabilities',
        //   // params:[address],
        // });
        // log('capabilities', capabilities);

        this.requireEthBalance = false; // Magic spend // https://www.smartwallet.dev/guides/magic-spend
        this.isSmartWallet = true;
        trackEvent('createdWallet');
        this.isConnecting = false;
      } catch (error) {
        this.isConnecting = false;
        this.handleError(error);
      }
    },
    getBaseName: async function (address, baseProvider) {
      try {
        const addressReverseNode = utils.convertReverseNodeToBytes(address, 8453); // 8453 is base chain id
        const contract = new ethers.Contract(BASENAME_L2_RESOLVER_ADDRESS, constants.L2ResolverAbi, baseProvider);
        const basename = await contract.name(addressReverseNode);
        return basename;
      } catch (e) {
        console.error('Error getting basename', e)
      }
    },
    getEnsName: async function(address) {
      const ensName = await infuraProvider.lookupAddress(address);
      return ensName;
    },
    getEnsAvatar: async function(ensName) {
      const avatar = await infuraProvider.getAvatar(ensName);
      log(`Avatar for ${ensName} URL: ${avatar}`);
      return avatar;
    },
    getEnsWebsite: async function(ensName) {
      const resolver = await infuraProvider.getResolver(ensName);
      if (resolver) {
        const website = await resolver.getText("url");
        return website;
      }
      return null;
    },
    // resolveEnsAvatar: async function(ensName, provider) {
    //   const resolver = await ethers.EnsResolver.fromName(provider, ensName);
    //   console.log('resolver', resolver)
    //   if (resolver) {
    //       // Resolve the avatar
    //       const avatarUrl = await resolver.getAvatar();
    //       if (avatarUrl) {
    //           console.log(`Avatar URL: ${avatarUrl}`);
    //       } else {
    //           console.log("No avatar found for this ENS name.");
    //       }
    //   } else {
    //       console.log("ENS name not found.");
    //   }
    // },
    setConnectedAccount: async function(account) {
      log('setConnectedAccount', account);
      if (account && this.connectAccountAddress && account.toLowerCase() !== this.connectAccountAddress.toLowerCase()) {
        // A different account: drop the previous one's signer and profile.
        this.resetProviderSigner();
        this.connectAccountEnsName = '';
        this.connectAccountEnsNameTrunc = '';
        this.connectAccountEnsAvatar = '';
        this.connectAccountEnsWebsite = '';
      }
      if(account) {
        // Hide modal
        this.$bvModal.hide('modal-connect');
        this.connectAccountAddress = account;
        this.connectAccountAddressTrunc = account.substring(0, 6) + '…' + account.substring(account.length - 4);

        if (this.$refs.routerViewRef) {
          this.$refs.routerViewRef.$emit('accountConnected', { message: 'Hello from parent' });
        }

        await this.ensureBaseChain(this.provider);

        // Update pixel price for NFT
        await this.updatePixelPrice();
        // Check if they have BITPIX to pay with
        await this.checkCanPayWithToken();

        let { provider, signer } = await this.getProviderSigner();

        const baseName = await this.getBaseName(account, signer);
        if (baseName) {
          this.setEnsName(baseName, 'baseName');
          const ensAvatar = await this.getEnsAvatar(this.connectAccountEnsName);
          this.connectAccountEnsAvatar = ensAvatar || '';
          const ensWebsite = await this.getEnsWebsite(this.connectAccountEnsName);
          if (ensWebsite) {
            this.connectAccountEnsWebsite = ensWebsite;
            this.formInputUrl = ensWebsite;
          } else {
            log(`No website found for address ${this.connectAccountEnsName}`);
            this.connectAccountEnsWebsite = '';
          }
        } else {
          log(`No base name found for address ${this.connectAccountAddress}`);
          const ensName = await this.getEnsName(this.connectAccountAddress);
          if (ensName) {
            this.setEnsName(ensName, 'ensName');
            const ensAvatar = await infuraProvider.getAvatar(this.connectAccountEnsName);
            this.connectAccountEnsAvatar = ensAvatar;
            const resolver = await infuraProvider.getResolver(ensName);
            if (resolver) {
              const website = await resolver.getText("url");
              if (website) {
                log(`The website for this.connectAccountEnsName ${this.connectAccountEnsName} is ${website}`);
                this.connectAccountEnsWebsite = website;
                this.formInputUrl = website;
              } else {
                log(`No website found for address ${this.connectAccountEnsName}`);
                this.connectAccountEnsWebsite = '';
              }
            } else {
              this.connectAccountEnsWebsite = '';
            }
          }
        }
        this.saveEvent('connectedAccount', {
        });
      } else {
        this.connectAccountAddress = '';
        this.connectAccountAddressTrunc = '';
        this.connectAccountEnsName = '';
        this.connectAccountEnsNameTrunc = '';
        this.connectAccountEnsAvatar = '';
        this.connectAccountEnsWebsite = '';
        this.browserProvider = null;
        this.provider = null;
      }
    },
    setEnsName: function(ensName, source) {
      this.connectAccountEnsName = ensName;
      if (this.connectAccountEnsName) {
        this.connectAccountEnsNameTrunc = this.connectAccountEnsName.length > 20 ? this.connectAccountEnsName.substring(0, 20) + '…' : this.connectAccountEnsName;
      } else {
        this.connectAccountEnsNameTrunc = '';
      }
      utils.trackEvent(source+':'+ensName);
      log(source, ensName);
    },
    doConnect: async function(provider) {
      // console.log(provider);
      log('do connect', provider);
      this.isConnecting = true;
      this.isSmartWallet = false;
      this.provider = provider.provider;
      try {
        const accounts = await this.provider
          .request({ method: "eth_requestAccounts" });
        this.setConnectedAccount(accounts[0]);
        // this.ensureBaseChain(this.provider);
        this.isConnecting = false;
      } catch (error) {
        console.error("Failed to connect to provider:", error)
        this.isConnecting = false;
      }
    },
    doDisconnect: async function() {
      if (!this.provider) {
        await this.setConnectedAccount(null);
        return;
      }
      try {
        await this.provider.request({ method: "wallet_revokePermissions", "params": [
          {
            "eth_accounts": {}
          }
        ] });
      } catch(e) {
        try {
          this.provider.close();
        } catch(e) {
          console.log('Couldnt close wallet');
        }
      }
      // this.provider = null;
      // this.getWalletPermissions(this.provider);
      log('Disconnect provider', this.provider);
      if (this.provider.disconnect) {
        await this.provider.disconnect();
      }
      await this.setConnectedAccount(null);
    },
    getWalletPermissions: async function(provider, accounts) {
      if (accounts && accounts.length > 0) {
        this.provider = provider;
        this.setConnectedAccount(accounts[0]);
        // this.ensureBaseChain(provider);
        return;
      }
      if (provider.isCoinbaseBrowser || provider.isCoinbaseWallet) {
        // Not supported
        return;
      }
      const permissions = await provider.request({ method: "wallet_getPermissions" });
      log('getWalletPermissions', permissions);
      accounts = permissions.find(p => p.parentCapability === "eth_accounts");
      log('accounts', accounts);
      if (!accounts || !accounts.caveats) {
        // log('Disconnect provider', provider);
        // if (provider.disconnect) {
        //   provider.disconnect();
        // }
        // this.setConnectedAccount(null);
      } else {
        let eth_accounts = accounts.caveats.find(c => c.type === "restrictReturnedAccounts");
        if (eth_accounts) {
          log('eth accounts', eth_accounts);
          this.provider = provider;
          this.setConnectedAccount(eth_accounts.value[0]);
        } else {
          this.setConnectedAccount(null);
        }
      }
    },
    parseChainId: function(chainId) {
      if (typeof chainId === 'string') {
        if (chainId.startsWith('0x')) {
          return parseInt(chainId, 16).toString();
        } else {
          return chainId;
        }
      } else if (typeof chainId === 'number') {
        return chainId.toString();
      }
    },
    isBaseChain: function(chainId) {
      log('isBaseChain', chainId, typeof chainId, BASE_CHAIN_ID, typeof BASE_CHAIN_ID, this.parseChainId(chainId), this.parseChainId(BASE_CHAIN_ID));
      return this.parseChainId(chainId) === this.parseChainId(BASE_CHAIN_ID);
    },
    switchToBaseChain: function(event) {
      this.ensureBaseChain(this.provider);
    },
    ensureBaseChain: async function(provider) {
      const chainId = await this.getEthChainId(provider);
      this.connectAccountChainId = chainId;
      if (!this.isBaseChain(chainId)) {
        log(`Switching from chain ${chainId} to base ${BASE_CHAIN_ID}`)
        try {
          await provider.request({
            "method": "wallet_switchEthereumChain",
            "params": [{ "chainId": BASE_CHAIN_ID.toString() }],
          });
        } catch (e) {
          if (!isUnknownChainError(e)) {
            throw e;
          }
          log('Wallet does not know Base yet, adding it');
          await provider.request({
            "method": "wallet_addEthereumChain",
            "params": [BASE_CHAIN_PARAMS],
          });
        }
        // Not every wallet emits chainChanged (e.g. the Coinbase SDK provider).
        this.connectAccountChainId = await this.getEthChainId(provider);
        this.resetProviderSigner();
      }
    },
    getEthChainId: async function(provider) {
      const chainId = await provider.request({ method: "eth_chainId", "params": [] });
      return chainId;
    },
    watchToken: async function() {
      await this.provider.request({
        "method": "wallet_watchAsset",
        "params": {
          "type": "ERC20",
          "options": {
            "address": config.TOKEN_CA,
            "symbol": this.symbol,
            "decimals": 18,
            "image": TOKEN_IMAGE_URL
          }
        }
      });
    },
    getTokenBalance: async function(p) {
      let provider = new ethers.BrowserProvider(p);
      let signer = await provider.getSigner();
      const contract = new ethers.Contract(this.contractAddress, [
        "function balanceOf(address) view returns (uint256)"
      ], signer);
      const balance = await contract.balanceOf(this.connectAccountAddress);
      log('token balance', balance.toString());
      this.tokenBalance = balance.toString();
      return balance;
    },
    // The cached ethers provider/signer are bound to one account and network.
    resetProviderSigner: function() {
      this.browserProvider = null;
      this.browserProviderSigner = null;
    },
    getProviderSigner: async function() {
      await this.ensureBaseChain(this.provider);
      if(!this.browserProvider) {
        this.browserProvider = await new ethers.BrowserProvider(this.provider);
        // if(DO_LOG) {
        //   this.browserProvider.on("debug", log);
        // }
        this.browserProviderSigner = await this.browserProvider.getSigner();
      }
      log('getProviderSigner', this.browserProvider, this.browserProviderSigner);
      return {
        provider: this.browserProvider,
        signer: this.browserProviderSigner
      }
    },
    hasProvider: function(name) {
      return this.providers.find(p => p.info.name === name);
    },
    pushProvider: function(p) {
      log('Found provider', p);
      if (!listenedProviders.has(p)) {
        listenedProviders.add(p);
        p.on("accountsChanged", (accounts) => {
          log('accountsChanged', accounts);
          // Once connected, only the wallet in use may change the session.
          if (this.provider && this.provider !== p) {
            return;
          }
          this.resetProviderSigner();
          this.getWalletPermissions(p, accounts);
        });
        p.on("chainChanged", (chainId) => {
          log('chainChanged', chainId, typeof chainId);
          if (this.provider && this.provider !== p) {
            return;
          }
          this.resetProviderSigner();
          this.connectAccountChainId = chainId;
        });
      }
      // p.on("disconnect", (error) => {
      //   log('disconnect', error);
      //   this.getWalletPermissions(event.detail.provider);
      // });
      if (p.isPhantom) {
        log('Phantom provider', p);
        if (!this.hasProvider('Phantom')) {
          this.providers.push({
            provider: p,
            info: {
              "uuid": "1",
              "name": "Phantom",
              "icon": "data:image/png;base64,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\n",
              "rdns": "app.phantom"
            }
          });
        }
        this.getWalletPermissions(p);
        trackEvent('hasWallet:app.phantom');
        return;
      }
      if (p.isMetaMask) {
        if (p.isRabby) {
          log('Rabby provider', p);
          this.providers.push({
            provider: p,
            info: {
                "uuid": "aba7b19c-2e24-4405-aff2-69dbd5bbfafd",
                "name": "Rabby",
                "icon": "data:image/svg+xml;base64,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",
                "rdns": "io.rabby"
            }
          });
          this.getWalletPermissions(p);
          trackEvent('hasWallet:io.rabby');
          return;
        }
        log('MetaMask provider', p);
        this.providers.push({
          provider: p,
          info:{
              "uuid": "eb90892c-6a12-44c9-9aea-31a4dd915385",
              "name": "MetaMask",
              "icon": "data:image/svg+xml;base64,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",
              "rdns": "io.metamask"
          }
        });
        this.getWalletPermissions(p);
        trackEvent('hasWallet:io.metamask');
      } else if (p.isCoinbaseWallet || p.isCoinbaseBrowser) {
        log('Coinbase Wallet provider', p);
        this.providers.push({
          provider: p,
          info: {
            "uuid": "22c494ca-e7ca-4c13-a5c0-6d72312046a6",
            "name": "Coinbase",
            "icon": "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTYiIGhlaWdodD0iNTYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTI4IDU2YzE1LjQ2NCAwIDI4LTEyLjUzNiAyOC0yOFM0My40NjQgMCAyOCAwIDAgMTIuNTM2IDAgMjhzMTIuNTM2IDI4IDI4IDI4WiIgZmlsbD0iIzFCNTNFNCIvPjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNNyAyOGMwIDExLjU5OCA5LjQwMiAyMSAyMSAyMXMyMS05LjQwMiAyMS0yMVMzOS41OTggNyAyOCA3IDcgMTYuNDAyIDcgMjhabTE3LjIzNC02Ljc2NmEzIDMgMCAwIDAtMyAzdjcuNTMzYTMgMyAwIDAgMCAzIDNoNy41MzNhMyAzIDAgMCAwIDMtM3YtNy41MzNhMyAzIDAgMCAwLTMtM2gtNy41MzNaIiBmaWxsPSIjZmZmIi8+PC9zdmc+",
            "rdns": "com.coinbase.wallet"
          }
        });
        trackEvent('hasWallet:com.coinbase.wallet');
      } else {
        log('Unknown provider', p);
        trackEvent('hasWallet:unknown');
      }
    },
    loadProviders: function() {
      if(this.providers.length === 0) {
        if (window.ethereum) {
          if(window.ethereum.providers && window.ethereum.providers.length) {
            for (let i = 0; i < window.ethereum.providers.length; i++) {
              const p = window.ethereum.providers[i];
              this.pushProvider(p);
            }
          } else {
            this.pushProvider(window.ethereum);
          }
        } else {
          // this.test_msg = 'no ether'
        }
      } else {
        // this.test_msg = 'has providers'
      }
    },
  },
};
